//#region header */
/**************************************************************************************************
//
//  Description: Form to display the elastic node information
//
//  Copyright:    © 2025 - 2026 Idox Software Limited
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//#region Version 1.0.0.0 changes
//    001   25.02.25 Sean Flook          GMSCM-1 Initial Revision.
//    002   05.02.26 Sean Flook         GMSCM-12 Changed required for the V2 of the API.
//#endregion Version 1.0.0.0 changes
//
//--------------------------------------------------------------------------------------------------
//#endregion header */

import React, { useEffect, useState } from "react";

import useConfigQueryStore from "../context/ConfigStore";
import useUserQueryStore from "../context/UserStore";

import ConfigErrorType from "../models/configErrorType";
import { MultiTenantConfigOrganisation } from "../dtos/multiTenantConfigDto";

import { Box, Grid2 } from "@mui/material";
import ADSTextControl from "../components/ADSTextControl";

import { dataFormStyle, FormRowStyle } from "../utils/ADSStyles";

interface ElasticNodeFormProps {
  parentTreeId: string;
  data: string;
  errors: ConfigErrorType[] | undefined;
  onDataChange: (data: string | undefined) => void;
}

const ElasticNodeForm: React.FC<ElasticNodeFormProps> = ({ parentTreeId, data, errors, onDataChange }) => {
  const currentUser = useUserQueryStore((s) => s.userQuery.currentUser);

  const revertChanges = useConfigQueryStore((s) => s.configQuery.revertChanges);
  const originalDocument = useConfigQueryStore((s) => s.configQuery.originalDocument);
  const organisationSelected = useConfigQueryStore((s) => s.configQuery.organisationSelected);
  const elasticNodeSelected = useConfigQueryStore((s) => s.configQuery.elasticNodeSelected);

  const [elasticNode, setElasticNode] = useState<string | undefined>();

  const [elasticNodeError, setElasticNodeError] = useState<string[] | undefined>();

  /**
   * Event to handle when the elastic node is changed.
   *
   * @param newValue The new elastic node.
   */
  const handleElasticNodeChangeEvent = (newValue: string | undefined): void => {
    setElasticNode(newValue);
    if (onDataChange) {
      onDataChange(newValue ?? "");
    }
  };

  useEffect(() => {
    setElasticNode(data ?? "");
  }, [data]);

  useEffect(() => {
    setElasticNodeError(undefined);

    if (errors && errors.length > 0) {
      for (const error of errors) {
        if (error.treeId === parentTreeId || error.id === parentTreeId) {
          switch (error.field) {
            case "elasticNode":
            case "elasticElasticNodes":
              setElasticNodeError(error.errors);
              break;

            default:
              break;
          }
        }
      }
    }
  }, [errors, parentTreeId]);

  useEffect(() => {
    if (revertChanges && originalDocument) {
      const originalOrganisation: MultiTenantConfigOrganisation = originalDocument.organisations.find(
        (x) => x.organisationName === organisationSelected
      ) as MultiTenantConfigOrganisation;

      if (originalOrganisation) {
        // const originalElasticNode = originalOrganisation.namedElasticSettings.SearchIndexConnection.elasticNodes.find(
        //   (x) => x === elasticNodeSelected
        // );
        // if (originalElasticNode) setElasticNode(originalElasticNode);
      }
    }
  }, [revertChanges, originalDocument, organisationSelected, elasticNodeSelected]);

  return (
    <Box sx={dataFormStyle("ElasticNodeForm")}>
      <Grid2 container justifyContent="flex-start" alignItems="baseline" sx={FormRowStyle()}>
        <Grid2 size={12}>
          <ADSTextControl
            label="Node"
            value={elasticNode} // Correct state variable
            maxLength={100}
            id="elastic_node"
            isEditable={currentUser?.isSuperAdministrator}
            isRequired
            helperText="The node URL for the elastic index."
            errorText={elasticNodeError}
            onChange={handleElasticNodeChangeEvent}
          />
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default ElasticNodeForm;
